import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useMediaAgentPoolConfigQuery } from '@/state/queries/media-agent-admin/use-media-agent-pool-config';
import { useUpdateMediaAgentPoolConfigMutation } from '@/state/mutations/media-agent-admin/use-update-media-agent-pool-config';
import type { MediaAgentPoolConfigDto } from '@/types/dtos/media-agent/media-agent-pool.dto';

type FormState = {
  baselineCount: string;
  autoProvisionEnabled: boolean;
  maxAutoProvision: string;
  vmStartRetryWindowMinutes: string;
  bufferHoursBefore: string;
  bufferHoursAfter: string;
  opsEmailRecipients: string;
  reservationCronTimezone: string;
};

function toFormState(config: MediaAgentPoolConfigDto): FormState {
  return {
    baselineCount: String(config.baselineCount),
    autoProvisionEnabled: config.autoProvisionEnabled,
    maxAutoProvision: String(config.maxAutoProvision),
    vmStartRetryWindowMinutes: String(config.vmStartRetryWindowMinutes),
    bufferHoursBefore: String(config.bufferHoursBefore),
    bufferHoursAfter: String(config.bufferHoursAfter),
    opsEmailRecipients: (config.opsEmailRecipients ?? []).join(', '),
    reservationCronTimezone: config.reservationCronTimezone,
  };
}

function normalizeOpsEmails(value: string): string {
  return value
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean)
    .join(', ');
}

function formStateEquals(a: FormState, b: FormState): boolean {
  return (
    a.baselineCount === b.baselineCount &&
    a.autoProvisionEnabled === b.autoProvisionEnabled &&
    a.maxAutoProvision === b.maxAutoProvision &&
    a.vmStartRetryWindowMinutes === b.vmStartRetryWindowMinutes &&
    a.bufferHoursBefore === b.bufferHoursBefore &&
    a.bufferHoursAfter === b.bufferHoursAfter &&
    normalizeOpsEmails(a.opsEmailRecipients) === normalizeOpsEmails(b.opsEmailRecipients) &&
    (a.reservationCronTimezone.trim() || 'Europe/Berlin') === (b.reservationCronTimezone.trim() || 'Europe/Berlin')
  );
}

export default function MediaAgentPoolConfigForm({ enabled }: { enabled: boolean }) {
  const t = useTranslations();
  const notification = useNotifications();
  const { data: config, isLoading, error } = useMediaAgentPoolConfigQuery({ enabled });
  const { mutateAsync: updateConfig, isPending } = useUpdateMediaAgentPoolConfigMutation();
  const [form, setForm] = useState<FormState | null>(null);
  const [expanded, setExpanded] = useState(false);
  const savedForm = useMemo(() => (config ? toFormState(config) : null), [config]);
  const isDirty = form !== null && savedForm !== null && !formStateEquals(form, savedForm);

  useEffect(() => {
    if (!config) return;
    setForm((prev) => {
      if (prev && savedForm && !formStateEquals(prev, savedForm)) {
        return prev;
      }
      return toFormState(config);
    });
  }, [config, savedForm]);

  if (!enabled) return null;
  if (isLoading || !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error">
        {t('admin.mediaAgentAdmin.configError', undefined, 'Failed to load pool configuration')}
      </Alert>
    );
  }

  const parseNumber = (value: string, field: string): number | null => {
    const trimmed = value.trim();
    if (!trimmed) {
      notification.notifyError(t('admin.mediaAgentAdmin.invalidNumber', { field }, `Invalid ${field}`));
      return null;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) {
      notification.notifyError(t('admin.mediaAgentAdmin.invalidNumber', { field }, `Invalid ${field}`));
      return null;
    }
    return n;
  };

  const onSave = async () => {
    const baselineCount = parseNumber(form.baselineCount, 'baseline count');
    const maxAutoProvision = parseNumber(form.maxAutoProvision, 'max auto-provision');
    const vmStartRetryWindowMinutes = parseNumber(form.vmStartRetryWindowMinutes, 'VM start retry window');
    const bufferHoursBefore = parseNumber(form.bufferHoursBefore, 'buffer hours before');
    const bufferHoursAfter = parseNumber(form.bufferHoursAfter, 'buffer hours after');
    if (
      vmStartRetryWindowMinutes !== null &&
      (!Number.isInteger(vmStartRetryWindowMinutes) ||
        vmStartRetryWindowMinutes < 5 ||
        vmStartRetryWindowMinutes > 1440)
    ) {
      notification.notifyError(
        t(
          'admin.mediaAgentAdmin.invalidVmStartRetryWindow',
          undefined,
          'VM start retry window must be a whole number between 5 and 1440 minutes',
        ),
      );
      return;
    }
    if (
      baselineCount === null ||
      maxAutoProvision === null ||
      vmStartRetryWindowMinutes === null ||
      bufferHoursBefore === null ||
      bufferHoursAfter === null
    ) {
      return;
    }

    const opsEmailRecipients = form.opsEmailRecipients
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);

    try {
      await updateConfig({
        baselineCount,
        autoProvisionEnabled: form.autoProvisionEnabled,
        maxAutoProvision,
        vmStartRetryWindowMinutes,
        bufferHoursBefore,
        bufferHoursAfter,
        opsEmailRecipients,
        reservationCronTimezone: form.reservationCronTimezone.trim() || 'Europe/Berlin',
      });
      notification.notifySuccess(t('admin.mediaAgentAdmin.configSaved', undefined, 'Pool configuration saved'));
    } catch {
      notification.notifyError(t('admin.mediaAgentAdmin.configSaveError', undefined, 'Failed to save configuration'));
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => {
        if (!next && isDirty) return;
        setExpanded(next);
      }}
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        'bgcolor': 'transparent',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ 'px': 0, 'minHeight': 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}
      >
        <Typography variant="subtitle2">
          {t('admin.mediaAgentAdmin.configTitle', undefined, 'Pool configuration')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              type="number"
              label={t('admin.mediaAgentAdmin.config.baselineCount', undefined, 'Baseline pool size')}
              value={form.baselineCount}
              onChange={(e) => setForm({ ...form, baselineCount: e.target.value })}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label={t('admin.mediaAgentAdmin.config.maxAutoProvision', undefined, 'Max auto-provision')}
              value={form.maxAutoProvision}
              onChange={(e) => setForm({ ...form, maxAutoProvision: e.target.value })}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Stack>
          <TextField
            size="small"
            type="number"
            label={t(
              'admin.mediaAgentAdmin.config.vmStartRetryWindowMinutes',
              undefined,
              'VM start retry window (minutes)',
            )}
            value={form.vmStartRetryWindowMinutes}
            onChange={(e) => setForm({ ...form, vmStartRetryWindowMinutes: e.target.value })}
            inputProps={{ min: 5, max: 1440, step: 1 }}
            helperText={t(
              'admin.mediaAgentAdmin.config.vmStartRetryWindowHint',
              undefined,
              'How long to keep retrying a VM start after a temporary Azure capacity failure (one attempt every 5 minutes)',
            )}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              type="number"
              label={t('admin.mediaAgentAdmin.config.bufferHoursBefore', undefined, 'Buffer hours before')}
              value={form.bufferHoursBefore}
              onChange={(e) => setForm({ ...form, bufferHoursBefore: e.target.value })}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label={t('admin.mediaAgentAdmin.config.bufferHoursAfter', undefined, 'Buffer hours after')}
              value={form.bufferHoursAfter}
              onChange={(e) => setForm({ ...form, bufferHoursAfter: e.target.value })}
              inputProps={{ min: 0 }}
              fullWidth
            />
          </Stack>
          <TextField
            size="small"
            label={t('admin.mediaAgentAdmin.config.opsEmails', undefined, 'Ops alert emails (comma-separated)')}
            value={form.opsEmailRecipients}
            onChange={(e) => setForm({ ...form, opsEmailRecipients: e.target.value })}
            fullWidth
            helperText={t(
              'admin.mediaAgentAdmin.config.opsEmailsHint',
              undefined,
              'Falls back to processor/VM alert email when empty',
            )}
          />
          <TextField
            size="small"
            label={t('admin.mediaAgentAdmin.config.timezone', undefined, 'Reservation cron timezone')}
            value={form.reservationCronTimezone}
            onChange={(e) => setForm({ ...form, reservationCronTimezone: e.target.value })}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.autoProvisionEnabled}
                onChange={(e) => setForm({ ...form, autoProvisionEnabled: e.target.checked })}
              />
            }
            label={t('admin.mediaAgentAdmin.config.autoProvision', undefined, 'Auto-provision on booking shortfall')}
          />
          {isDirty && (
            <Box>
              <Button variant="contained" size="small" disabled={isPending} onClick={() => void onSave()}>
                {isPending
                  ? t('admin.mediaAgentAdmin.configSaving', undefined, 'Saving…')
                  : t('admin.mediaAgentAdmin.configSave', undefined, 'Save configuration')}
              </Button>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
